import { createContext, useContext, useEffect, useId, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { formatMoment, formatMoney, inputFromMinor, minorFromInput, shortId } from './format';
import {
  IconChevronDown,
  IconChevronLeft,
  IconChevronRight,
  IconCopy,
  IconCheck,
  IconInbox,
  IconRefresh,
} from './icons';

/* ------------------------------------------------------------------ каркас */

/**
 * Ширина рабочей области и поля вокруг неё заданы здесь и больше нигде. Раньше
 * каждый модуль выбирал `p-6`, `p-4` или `px-6 pt-4` на глаз, и при переходе
 * между разделами содержимое заметно ёрзало по горизонтали.
 */
const CONTAINER = 'mx-auto w-full max-w-(--w-page) px-4 sm:px-6 lg:px-8';

export function PageHeader({
  title,
  subtitle,
  actions,
}: {
  title: string;
  subtitle?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <header className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2">
      <div className="min-w-0">
        <h1 className="text-lg font-semibold tracking-tight text-ink sm:text-xl">{title}</h1>
        {/* На телефоне пояснение к разделу съедало треть экрана над таблицей;
            смысл раздела там несёт название и полоса вкладок. */}
        {subtitle ? (
          <p className="mt-0.5 hidden text-sm text-ink-muted sm:block">{subtitle}</p>
        ) : null}
      </div>
      {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
    </header>
  );
}

export interface TabItem<K extends string> {
  readonly key: K;
  readonly label: string;
  readonly hint?: string;
}

/**
 * Полоса вкладок не переносится на вторую строку, а прокручивается: перенос
 * менял высоту шапки при смене раздела, и содержимое под ней прыгало.
 */
export function Tabs<K extends string>({
  items,
  active,
  onSelect,
}: {
  items: readonly TabItem<K>[];
  active: K;
  onSelect: (key: K) => void;
}) {
  return (
    <nav
      className="-mb-px flex gap-1 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      role="tablist"
    >
      {items.map((item) => {
        const current = item.key === active;
        return (
          <button
            key={item.key}
            type="button"
            role="tab"
            aria-selected={current}
            title={item.hint}
            onClick={() => onSelect(item.key)}
            className={`shrink-0 border-b-2 px-3 py-2 text-sm font-medium whitespace-nowrap transition ${
              current
                ? 'border-brand-600 text-brand-700'
                : 'border-transparent text-ink-muted hover:border-line-strong hover:text-ink'
            }`}
          >
            {item.label}
          </button>
        );
      })}
    </nav>
  );
}

/**
 * Короткий набор взаимоисключающих значений — период, режим. Раньше каждый
 * модуль рисовал его вручную, и наборы разъехались: в распознавании невыбранные
 * кнопки были с рамкой, в аналитике — без, а выбранная в обоих случаях красилась
 * акцентом заливкой, отчего спорила с главной кнопкой действия рядом.
 */
export function Segmented<K extends string>({
  items,
  active,
  onSelect,
  label,
}: {
  items: readonly { readonly key: K; readonly label: string }[];
  active: K;
  onSelect: (key: K) => void;
  label?: string;
}) {
  return (
    <div
      role="group"
      aria-label={label}
      className="inline-flex shrink-0 rounded-control border border-line bg-sunken p-0.5"
    >
      {items.map((item) => {
        const current = item.key === active;
        return (
          <button
            key={item.key}
            type="button"
            aria-pressed={current}
            onClick={() => onSelect(item.key)}
            className={`touch-exempt rounded-[0.25rem] px-2.5 py-1 text-sm font-medium whitespace-nowrap transition ${
              current ? 'bg-surface text-ink shadow-card' : 'text-ink-muted hover:text-ink'
            }`}
          >
            {item.label}
          </button>
        );
      })}
    </div>
  );
}

/**
 * Единый корень раздела: шапка с названием и вкладками липнет к верху, под ней —
 * колонка содержимого постоянной ширины с одинаковыми полями. До этого один
 * модуль рисовал `flex h-full flex-col` с собственной шапкой, другой — `p-6` со
 * своей прокруткой, третий вовсе без шапки; отсюда и разъезжались отступы.
 */
export function ModulePage<K extends string>({
  title,
  subtitle,
  actions,
  tabs,
  activeTab,
  onTab,
  children,
}: {
  title: string;
  subtitle?: ReactNode;
  actions?: ReactNode;
  tabs?: readonly TabItem<K>[];
  activeTab?: K;
  onTab?: (key: K) => void;
  children: ReactNode;
}) {
  const withTabs = tabs && tabs.length > 0 && activeTab !== undefined && onTab;
  return (
    <div className="flex min-h-full flex-col">
      <div
        className={`sticky top-0 z-20 border-b border-line bg-surface ${withTabs ? 'pt-4' : 'py-4'}`}
      >
        <div className={CONTAINER}>
          <PageHeader
            title={title}
            {...(subtitle ? { subtitle } : {})}
            {...(actions ? { actions } : {})}
          />
          {withTabs ? (
            <div className="mt-3">
              <Tabs items={tabs} active={activeTab} onSelect={onTab} />
            </div>
          ) : null}
        </div>
      </div>
      <div className={`${CONTAINER} flex-1 py-5 sm:py-6`}>
        <div className="flex flex-col gap-4 sm:gap-5">{children}</div>
      </div>
    </div>
  );
}

export function Card({
  title,
  hint,
  actions,
  children,
}: {
  title?: string;
  hint?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="rounded-card border border-line bg-surface shadow-card">
      {title || actions ? (
        <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-2 border-b border-line px-4 py-3">
          <div className="min-w-0">
            {title ? <h3 className="text-sm font-semibold text-ink">{title}</h3> : null}
            {hint ? <p className="mt-0.5 text-xs text-ink-muted">{hint}</p> : null}
          </div>
          {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
        </div>
      ) : null}
      <div className="p-4">{children}</div>
    </section>
  );
}

export function Toolbar({ children }: { children: ReactNode }) {
  return <div className="flex flex-wrap items-end gap-3">{children}</div>;
}

/**
 * Сетка карточек-показателей. Число колонок задаётся не модулем, а здесь: пять
 * колонок, прописанных без брейкпоинта, на телефоне оставались пятью колонками
 * шириной в палец.
 */
export function MetricGrid({ children }: { children: ReactNode }) {
  return <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-4">{children}</div>;
}

/** Две равные колонки на широком экране, одна — на узком. */
export function SplitGrid({ children }: { children: ReactNode }) {
  return <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">{children}</div>;
}

/* ------------------------------------------------------------- поля ввода */

/**
 * Идентификатор поля, который подпись отдаёт вниз своему полю ввода.
 *
 * Раньше `Field` оборачивал поле в `<label>` целиком, без `for`. Для текста это
 * работало, а для `<select>` ломалось: клик по самому списку получал две
 * активации подряд — родную и переадресованную меткой, — и выпадающий список
 * открывался и тут же закрывался. Снаружи это выглядело как мерцание.
 *
 * Теперь подпись и поле — соседи, связанные через `for`/`id`. Клик по подписи
 * по-прежнему ставит фокус в поле, но второй активации больше нет.
 */
const FieldIdContext = createContext<string | undefined>(undefined);

/** Идентификатор от ближайшего `Field`, если поле стоит внутри него. */
export function useFieldId(explicit?: string): string | undefined {
  const inherited = useContext(FieldIdContext);
  return explicit ?? inherited;
}

export function Field({
  label,
  hint,
  error,
  required,
  className = '',
  children,
}: {
  label: string;
  hint?: ReactNode;
  error?: string;
  required?: boolean;
  /** Только раскладка: место в сетке, доля ширины. Оформление — забота поля. */
  className?: string;
  children: ReactNode;
}) {
  const id = useId();
  return (
    <div className={`flex min-w-0 flex-col gap-1 text-sm ${className}`}>
      <label htmlFor={id} className="font-medium text-ink">
        {label}
        {required ? <span className="ml-0.5 text-danger">*</span> : null}
      </label>
      <FieldIdContext.Provider value={id}>{children}</FieldIdContext.Provider>
      {error ? (
        <span className="text-xs text-danger">{error}</span>
      ) : hint ? (
        <span className="text-xs text-ink-muted">{hint}</span>
      ) : null}
    </div>
  );
}

const INPUT =
  'w-full rounded-control border border-line-strong bg-surface px-2.5 py-1.5 text-sm text-ink outline-none transition placeholder:text-ink-faint hover:border-ink-faint focus:border-brand-500 disabled:cursor-not-allowed disabled:bg-sunken disabled:text-ink-faint';

export function TextInput(props: React.InputHTMLAttributes<HTMLInputElement>) {
  const id = useFieldId(props.id);
  return <input {...props} id={id} className={`${INPUT} ${props.className ?? ''}`} />;
}

export function TextArea(props: React.TextareaHTMLAttributes<HTMLTextAreaElement>) {
  const id = useFieldId(props.id);
  return <textarea {...props} id={id} className={`${INPUT} ${props.className ?? ''}`} />;
}

/**
 * Стрелка рисуется нами, а не браузером: системная стрелка приходит в цветах
 * системной темы и на тёмном фоне превращалась в чёрный треугольник на чёрном.
 */
export function Select({ className, ...props }: React.SelectHTMLAttributes<HTMLSelectElement>) {
  const id = useFieldId(props.id);
  return (
    <span className={`relative flex min-w-0 items-center ${className ?? ''}`}>
      <select {...props} id={id} className={`${INPUT} appearance-none pr-8`} />
      <IconChevronDown size={16} className="pointer-events-none absolute right-2 text-ink-faint" />
    </span>
  );
}

/**
 * Флажок. Родной, но с `accent-color`: галочка берёт акцент интерфейса и в тёмной
 * теме тоже, а размер задан один на все экраны — до этого он был то 16 px, то
 * системный по умолчанию, и строки фильтров стояли на разной высоте.
 */
export function Checkbox({
  className = '',
  ...props
}: React.InputHTMLAttributes<HTMLInputElement>) {
  const id = useFieldId(props.id);
  return (
    <input
      {...props}
      id={id}
      type="checkbox"
      className={`h-4 w-4 shrink-0 accent-brand-600 ${className}`}
    />
  );
}

/**
 * Деньги вводятся в тенге и уходят на сервер в тиынах. Отдельный компонент
 * появился потому, что поле «Цена, тиын» принимало тиыны, а колонка рядом
 * печатала тенге под тем же заголовком — расхождение в сто раз на одном экране.
 */
export function MoneyInput({
  valueMinor,
  onChangeMinor,
  ...props
}: Omit<React.InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange'> & {
  valueMinor: number | null;
  onChangeMinor: (minor: number | null) => void;
}) {
  const [draft, setDraft] = useState<string | null>(null);
  const id = useFieldId(props.id);
  const shown = draft ?? inputFromMinor(valueMinor);
  return (
    <span className="relative flex items-center">
      <input
        {...props}
        id={id}
        inputMode="decimal"
        value={shown}
        onChange={(e) => {
          setDraft(e.target.value);
          onChangeMinor(minorFromInput(e.target.value));
        }}
        onBlur={(e) => {
          setDraft(null);
          props.onBlur?.(e);
        }}
        className={`${INPUT} pr-7 tabular ${props.className ?? ''}`}
      />
      <span className="pointer-events-none absolute right-2.5 text-sm text-ink-faint">₸</span>
    </span>
  );
}

type ButtonTone = 'primary' | 'danger' | 'ghost' | 'quiet';
type ButtonSize = 'md' | 'sm';

const TONES: Record<ButtonTone, string> = {
  primary:
    'bg-brand-600 text-ink-invert shadow-card hover:bg-brand-700 disabled:bg-sunken disabled:text-ink-muted disabled:shadow-none disabled:shadow-none',
  danger:
    'bg-danger text-ink-invert shadow-card hover:opacity-90 disabled:bg-sunken disabled:text-ink-muted disabled:shadow-none',
  ghost: 'border border-line-strong text-ink hover:bg-sunken disabled:text-ink-faint',
  quiet: 'text-brand-700 hover:bg-brand-50 disabled:text-ink-faint',
};

const SIZES: Record<ButtonSize, string> = {
  md: 'h-8 px-3 text-sm',
  sm: 'h-7 px-2.5 text-xs',
};

export function Button({
  tone = 'ghost',
  size = 'md',
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & { tone?: ButtonTone; size?: ButtonSize }) {
  return (
    <button
      type="button"
      {...props}
      className={`inline-flex items-center justify-center gap-1.5 rounded-control font-medium whitespace-nowrap transition disabled:cursor-not-allowed ${TONES[tone]} ${SIZES[size]} ${props.className ?? ''}`}
    />
  );
}

/* -------------------------------------------------------------- состояния */

export function Notice({
  tone,
  title,
  children,
}: {
  tone: 'info' | 'warn' | 'error' | 'ok';
  title?: string;
  children?: ReactNode;
}) {
  const styles: Record<string, string> = {
    info: 'border-line bg-sunken text-ink',
    warn: 'border-warn-line bg-warn-bg text-warn',
    error: 'border-danger-line bg-danger-bg text-danger',
    ok: 'border-ok-line bg-ok-bg text-ok',
  };
  return (
    <div className={`rounded-control border px-3 py-2 text-sm ${styles[tone]}`}>
      {title ? <p className="font-medium">{title}</p> : null}
      {children ? <div className={title ? 'mt-0.5' : ''}>{children}</div> : null}
    </div>
  );
}

/**
 * Пустой экран обязан объяснять, почему он пуст, и что с этим делать. «Данных нет»
 * без причины неотличимо от поломки: на вкладке задержек прочерк означал «нет
 * подтверждений отрисовки с планшетов», но прочитать это было неоткуда.
 */
export function EmptyState({
  title,
  reason,
  action,
}: {
  title: string;
  reason?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center gap-2 rounded-card border border-dashed border-line px-4 py-10 text-center">
      <IconInbox size={26} className="text-ink-faint" />
      <p className="text-sm font-medium text-ink">{title}</p>
      {reason ? (
        <p className="max-w-(--w-prose) text-sm text-balance text-ink-muted">{reason}</p>
      ) : null}
      {action ? <div className="mt-1">{action}</div> : null}
    </div>
  );
}

export function Skeleton({ className = 'h-8' }: { className?: string }) {
  return <div className={`animate-pulse rounded-control bg-sunken ${className}`} />;
}

/**
 * Скелет вместо надписи «Загрузка…»: полосы держат высоту будущего содержимого,
 * поэтому страница не подпрыгивает в момент, когда данные приходят.
 */
export function LoadingState({ label = 'Загружаем данные' }: { label?: string }) {
  return (
    <div className="flex flex-col gap-2" aria-busy="true" aria-live="polite">
      <span className="sr-only">{label}…</span>
      <Skeleton className="h-4 w-40" />
      {[0, 1, 2, 3].map((row) => (
        <Skeleton key={row} />
      ))}
    </div>
  );
}

/**
 * Скелет таблицы: шапка и несколько строк той же высоты, что будут у данных.
 *
 * Нужен там, где список грузится: без него экран показывает «записей нет» —
 * то есть отвечает на вопрос, на который ответа ещё не знает. Человек читает
 * это как «здесь пусто» и уходит.
 */
export function TableSkeleton({
  rows = 5,
  columns = 4,
  label = 'Загружаем данные',
}: {
  rows?: number;
  columns?: number;
  label?: string;
}) {
  return (
    <div
      className="overflow-hidden rounded-card border border-line bg-surface"
      aria-busy="true"
      aria-live="polite"
    >
      <span className="sr-only">{label}…</span>
      <div className="flex gap-4 border-b border-line bg-sunken px-3 py-2">
        {Array.from({ length: columns }, (_, i) => (
          <Skeleton key={i} className="h-3 flex-1" />
        ))}
      </div>
      {Array.from({ length: rows }, (_, row) => (
        <div key={row} className="flex gap-4 border-b border-line px-3 py-3 last:border-0">
          {Array.from({ length: columns }, (_, col) => (
            <Skeleton
              key={col}
              // Разная ширина ячеек: ровные полосы читаются как разметка, а не
              // как «данные едут».
              className={`h-4 flex-1 ${col === 0 ? 'max-w-[40%]' : ''}`}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

/**
 * Скелет строками — для таблиц, собранных руками из `<Table>`, а не из
 * `DataTable`. Вставляется в `<tbody>` перед `map` по данным и не требует
 * перестраивать разметку: пока данных нет, вместо пустого места видно, что
 * они едут.
 */
export function SkeletonRows({
  rows = 4,
  columns = 4,
  label = 'Загружаем данные',
}: {
  rows?: number;
  columns?: number;
  label?: string;
}) {
  return (
    <>
      <tr className="sr-only">
        <td colSpan={columns} aria-busy="true" aria-live="polite">
          {label}…
        </td>
      </tr>
      {Array.from({ length: rows }, (_, row) => (
        <tr key={`skeleton-${row}`} className="border-t border-line">
          {Array.from({ length: columns }, (_, col) => (
            <td key={col} className="py-2 pr-3">
              <Skeleton className={`h-4 ${col === 0 ? 'w-32' : 'w-20'}`} />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}

/** Полоска ожидания над содержимым, которое уже показано и сейчас обновится. */
export function InlineLoader({ label = 'Обновляем' }: { label?: string }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-sm text-ink-muted" aria-live="polite">
      <IconRefresh size={14} className="animate-spin" />
      {label}…
    </span>
  );
}

/**
 * Панель, которая появляется по нажатию, — с прокруткой к себе.
 *
 * Формы возврата, правки правила и подобные раньше открывались там, где стоят
 * в разметке: под длинной таблицей, то есть за пределами экрана. Человек жал
 * «Возврат», ничего не происходило, и он жал ещё раз.
 *
 * Прокрутка идёт в центр окна, а не к верхнему краю: шапка раздела `sticky`,
 * и выравнивание по верху спрятало бы первую строку панели под ней.
 */
export function Reveal({
  when,
  children,
}: {
  /** Значение, при смене которого панель считается открытой заново. */
  readonly when?: unknown;
  readonly children: ReactNode;
}) {
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    // Панель может быть всегда в разметке и «открываться» появлением значения.
    // Пока значения нет, прокручивать не к чему — иначе страница дёргалась бы
    // при первой отрисовке.
    if (when === undefined || when === null || when === false || when === '') return;

    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
    node.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'center' });

    // Фокус — на первое поле ввода, но без своей прокрутки: браузер увёл бы
    // панель к краю и сломал бы центрирование выше.
    const first = node.querySelector<HTMLElement>('input, select, textarea');
    first?.focus({ preventScroll: true });
  }, [when]);

  return (
    <div ref={ref} className="scroll-mt-28">
      {children}
    </div>
  );
}

/** Экран до входа: приложение ещё не знает, кто пришёл, и рисовать нечего. */
export function SplashState({ label }: { label: string }) {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-3 bg-canvas">
      <IconRefresh size={22} className="animate-spin text-ink-faint" />
      <p className="text-sm text-ink-muted">{label}…</p>
    </div>
  );
}

export function ErrorState({
  error,
  onRetry,
}: {
  error: { readonly message: string; readonly code: string; readonly forbidden: boolean };
  onRetry?: () => void;
}) {
  if (error.forbidden) {
    return (
      <Notice tone="warn" title="Раздел недоступен для вашей роли">
        {error.message}
      </Notice>
    );
  }
  return (
    <Notice tone="error" title="Не удалось загрузить данные">
      <p>{error.message}</p>
      <p className="mt-1 font-mono text-xs opacity-70">{error.code}</p>
      {onRetry ? (
        <button
          type="button"
          onClick={onRetry}
          className="mt-2 inline-flex items-center gap-1.5 underline underline-offset-2"
        >
          <IconRefresh size={14} />
          Повторить
        </button>
      ) : null}
    </Notice>
  );
}

/**
 * Единая обёртка «загрузка / ошибка / данные»: раньше каждый модуль писал эту
 * тройку сам, и ветка ошибки часто забывала снять «Загрузка…».
 */
export function Resourceful<T>({
  resource,
  children,
  loadingLabel,
}: {
  resource: {
    readonly status: 'loading' | 'ready' | 'error';
    readonly data: T | undefined;
    readonly error: { message: string; code: string; forbidden: boolean } | undefined;
    readonly reload: () => void;
  };
  children: (data: T) => ReactNode;
  loadingLabel?: string;
}) {
  if (resource.status === 'error' && resource.error) {
    return <ErrorState error={resource.error} onRetry={resource.reload} />;
  }
  if (resource.data === undefined) {
    return <LoadingState {...(loadingLabel ? { label: loadingLabel } : {})} />;
  }
  return <>{children(resource.data)}</>;
}

/* --------------------------------------------------------------- значения */

export type BadgeTone = 'neutral' | 'ok' | 'warn' | 'danger' | 'brand';

const BADGE: Record<BadgeTone, string> = {
  neutral: 'border-line bg-sunken text-ink-muted',
  ok: 'border-ok-line bg-ok-bg text-ok',
  warn: 'border-warn-line bg-warn-bg text-warn',
  danger: 'border-danger-line bg-danger-bg text-danger',
  brand: 'border-brand-300 bg-brand-50 text-brand-700',
};

export function StatusBadge({
  tone = 'neutral',
  children,
}: {
  tone?: BadgeTone;
  children: ReactNode;
}) {
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium whitespace-nowrap ${BADGE[tone]}`}
    >
      {children}
    </span>
  );
}

export function Money({ minor }: { minor: number | null | undefined }) {
  return <span className="tabular whitespace-nowrap">{formatMoney(minor)}</span>;
}

export function Moment({ iso }: { iso: string | null | undefined }) {
  if (!iso) return <span className="text-ink-faint">—</span>;
  return <span className="tabular whitespace-nowrap">{formatMoment(iso)}</span>;
}

/**
 * Идентификатор — служебное значение, а не содержание строки. Показываем хвост,
 * полный ULID отдаём по наведению и по кнопке копирования: список пар дублей
 * вида «JC0EY4VA ↔ YDVJ8K2K» не отвечал на вопрос, кто эти люди, но и совсем
 * прятать идентификатор нельзя — он нужен для разбора.
 */
export function Id({ value, label }: { value: string | null | undefined; label?: string }) {
  const [copied, setCopied] = useState(false);
  if (!value) return <span className="text-ink-faint">—</span>;
  return (
    <span className="inline-flex items-center gap-1">
      {label ? <span className="text-ink">{label}</span> : null}
      <button
        type="button"
        title={`${value} — скопировать`}
        onClick={() => {
          void navigator.clipboard?.writeText(value).then(() => {
            setCopied(true);
            window.setTimeout(() => setCopied(false), 1200);
          });
        }}
        className="touch-exempt inline-flex items-center gap-1 rounded bg-sunken px-1.5 py-0.5 font-mono text-xs text-ink-muted transition hover:text-ink"
      >
        {copied ? <IconCheck size={12} className="text-ok" /> : <IconCopy size={12} />}
        {copied ? 'скопировано' : shortId(value)}
      </button>
    </span>
  );
}

/**
 * Показатель с формулой на месте. Спор «как ты это считал» гарантирован, если
 * рядом с числом не написано, из чего оно сложилось.
 */
export function Metric({
  label,
  value,
  formula,
  source,
  tone = 'neutral',
  note,
}: {
  label: string;
  value: ReactNode;
  formula?: string;
  source?: string;
  tone?: BadgeTone;
  note?: ReactNode;
}) {
  const accent: Record<BadgeTone, string> = {
    neutral: 'text-ink',
    ok: 'text-ok',
    warn: 'text-warn',
    danger: 'text-danger',
    brand: 'text-brand-700',
  };
  return (
    <div className="rounded-card border border-line bg-surface p-3.5 shadow-card">
      <p className="text-[11px] font-medium tracking-wider text-ink-muted uppercase">{label}</p>
      <p className={`mt-1 text-xl font-semibold tabular sm:text-2xl ${accent[tone]}`}>{value}</p>
      {formula ? <p className="mt-1.5 text-xs text-ink-muted">Как считаем: {formula}</p> : null}
      {source ? (
        <p className="mt-0.5 font-mono text-xs text-ink-faint">Источник: {source}</p>
      ) : null}
      {note ? <div className="mt-1.5 text-xs text-ink-muted">{note}</div> : null}
    </div>
  );
}

/* --------------------------------------------------------------- таблицы */

/**
 * Постраничный показ длинных списков.
 *
 * Триста строк одним полотном — это не «всё видно», а «ничего не найти»: браузер
 * рисует их все, полоса прокрутки становится волоском, а вопрос «сколько всего»
 * остаётся без ответа. Считаем на клиенте: списки приходят с сервера целиком,
 * серверная постраничность — отдельный разговор с ядром, и без неё лучше честно
 * показывать «1–25 из 190», чем молча рисовать сто девяносто строк.
 */
const PAGE_SIZES = [25, 50, 100] as const;

export interface Paged<T> {
  readonly page: number;
  readonly pages: number;
  readonly size: number;
  readonly total: number;
  readonly rows: readonly T[];
  readonly setPage: (page: number) => void;
  readonly setSize: (size: number) => void;
}

export function usePagination<T>(rows: readonly T[], initialSize = 25): Paged<T> {
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(initialSize);
  const total = rows.length;
  const pages = Math.max(1, Math.ceil(total / size));
  const current = Math.min(page, pages);

  // Отфильтровали список — возвращаемся на первую страницу: иначе после фильтра
  // «Отклонённые» человек видит пустую седьмую страницу и решает, что их нет.
  useEffect(() => {
    setPage(1);
  }, [total, size]);

  const from = (current - 1) * size;
  return {
    page: current,
    pages,
    size,
    total,
    rows: total > size ? rows.slice(from, from + size) : rows,
    setPage,
    setSize,
  };
}

export function Pagination<T>({ paged, noun }: { paged: Paged<T>; noun?: string }) {
  if (paged.total <= PAGE_SIZES[0] && paged.total <= paged.size) return null;
  const from = (paged.page - 1) * paged.size + 1;
  const to = Math.min(paged.total, paged.page * paged.size);

  const step = (delta: number) =>
    paged.setPage(Math.min(paged.pages, Math.max(1, paged.page + delta)));

  return (
    <nav
      className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 border-t border-line pt-3 text-sm"
      aria-label="Страницы"
    >
      <p className="text-ink-muted tabular">
        {from}–{to} из {paged.total}
        {noun ? ` ${noun}` : ''}
      </p>

      <div className="flex items-center gap-2">
        <label className="flex items-center gap-1.5 text-xs text-ink-muted">
          На странице
          <Select
            aria-label="Строк на странице"
            value={String(paged.size)}
            onChange={(e) => paged.setSize(Number(e.target.value))}
            className="w-20"
          >
            {PAGE_SIZES.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </Select>
        </label>

        <div className="flex items-center gap-1">
          <Button
            size="sm"
            aria-label="Предыдущая страница"
            disabled={paged.page <= 1}
            onClick={() => step(-1)}
          >
            <IconChevronLeft size={14} />
          </Button>
          <span className="px-1 text-xs text-ink-muted tabular whitespace-nowrap">
            {paged.page} / {paged.pages}
          </span>
          <Button
            size="sm"
            aria-label="Следующая страница"
            disabled={paged.page >= paged.pages}
            onClick={() => step(1)}
          >
            <IconChevronRight size={14} />
          </Button>
        </div>
      </div>
    </nav>
  );
}

export interface Column<T> {
  readonly key: string;
  readonly header: string;
  readonly render: (row: T) => ReactNode;
  readonly width?: string;
  /** Числовые колонки прижимаются вправо — так столбик читается. */
  readonly numeric?: boolean;
}

/**
 * На узком экране таблица перестаёт быть таблицей и раскладывается в карточки —
 * заголовок колонки уходит подписью слева от значения (см. `.data-table` в
 * index.css). Горизонтальная прокрутка таблицы на телефоне — это способ
 * гарантированно спрятать колонку «Статус» за правым краем экрана.
 */
export function DataTable<T>({
  rows,
  columns,
  empty,
  emptyReason,
  rowKey,
  onRowClick,
  activeKey,
  pageSize = 25,
  noun,
  loading = false,
}: {
  rows: readonly T[];
  columns: ReadonlyArray<Column<T>>;
  empty: string;
  emptyReason?: ReactNode;
  rowKey: (row: T) => string;
  onRowClick?: (row: T) => void;
  activeKey?: string;
  /** Строк на странице. Пагинация появляется, только когда строк больше. */
  pageSize?: number;
  /** Слово для счётчика: «из 190 обращений». */
  noun?: string;
  /**
   * Данные ещё едут. Пока их нет, таблица показывает скелет, а не «записей
   * нет»: пустой ответ и незаконченный запрос — разные вещи, и путать их
   * нельзя. Человек читает «записей нет» как ответ и уходит со страницы.
   */
  loading?: boolean;
}) {
  const paged = usePagination(rows, pageSize);

  if (loading) {
    return <TableSkeleton rows={Math.min(pageSize, 6)} columns={columns.length} />;
  }

  if (rows.length === 0) {
    return <EmptyState title={empty} {...(emptyReason ? { reason: emptyReason } : {})} />;
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="data-table-wrap -mx-1 px-1">
        <table className="data-table w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-line-strong text-left text-[11px] font-medium tracking-wider text-ink-muted uppercase">
              {columns.map((c) => (
                <th
                  key={c.key}
                  className={`px-2 py-2 font-medium whitespace-nowrap ${c.numeric ? 'text-right' : ''}`}
                  style={c.width ? { width: c.width } : undefined}
                >
                  {c.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {paged.rows.map((row) => {
              const key = rowKey(row);
              return (
                <tr
                  key={key}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  className={`border-b border-line align-top transition-colors ${
                    activeKey === key ? 'bg-brand-50' : 'hover:bg-sunken'
                  } ${onRowClick ? 'cursor-pointer' : ''}`}
                >
                  {columns.map((c) => (
                    <td
                      key={c.key}
                      data-label={c.header}
                      className={`px-2 py-2 text-ink ${c.numeric ? 'text-right' : ''}`}
                    >
                      {c.render(row)}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <Pagination paged={paged} {...(noun ? { noun } : {})} />
    </div>
  );
}

/**
 * Обёртка для таблиц, которые собираются вручную (со своими действиями в строке,
 * раскрытием и подытогами) и потому не проходят через `DataTable`. Даёт им ровно
 * то, чего им не хватало: собственную горизонтальную прокрутку, чтобы широкая
 * таблица не растягивала страницу и не выносила колонку «Статус» за экран, и общее
 * оформление шапки — раньше половина таблиц печатала заголовки строчными серыми,
 * половина капителью, и на соседних вкладках это бросалось в глаза.
 *
 * Раскладку в карточки на телефоне такие таблицы не получают: для неё нужен
 * заголовок колонки на каждой ячейке, а его знает только `DataTable`.
 */
export function Table({ className = '', children }: { className?: string; children: ReactNode }) {
  return (
    <div className="-mx-1 overflow-x-auto px-1">
      <table className={`moika-table w-full border-collapse text-sm ${className}`}>
        {children}
      </table>
    </div>
  );
}

/**
 * Панель сравнения и подобные детали раньше рисовались под таблицей — за пределами
 * экрана, из-за чего казалось, что кнопка не сработала. Выдвижная панель всегда
 * в поле зрения и закрывается по Esc.
 */
export function Drawer({
  title,
  onClose,
  children,
  footer,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
}) {
  // Комментарий выше обещал закрытие по Esc, но обработчика не было ни здесь, ни
  // у вызывающих: панель ловилась только мышью по затемнению.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-40 flex justify-end bg-scrim"
      style={{ animation: 'moika-overlay-in 120ms ease-out' }}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <aside
        className="flex h-full w-[min(46rem,100%)] flex-col border-l border-line bg-surface shadow-sheet"
        style={{ animation: 'moika-sheet-right 180ms ease-out' }}
        role="dialog"
        aria-label={title}
      >
        <header className="flex items-center justify-between gap-3 border-b border-line px-4 py-3">
          <h2 className="text-sm font-semibold text-ink">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-control px-2 py-1 text-lg leading-none text-ink-muted transition hover:bg-sunken hover:text-ink"
            aria-label="Закрыть"
          >
            ×
          </button>
        </header>
        <div className="min-h-0 flex-1 overflow-y-auto p-4">{children}</div>
        {footer ? <footer className="border-t border-line px-4 py-3">{footer}</footer> : null}
      </aside>
    </div>
  );
}
